import React, { useMemo } from "react";
import {
  Stack,
  Heading,
  Text,
  Box,
  Button,
  Column,
  Columns,
} from "@origin-digital/ods-core";
import { useFormikContext } from "formik";
import { EICFormData } from "./types";
import { ConnectionTypeEnum, State } from "src/components/Premises/types";
import ReadOnlyTextField from "../FormComponents/ReadOnlyTextField";
import {
  getConnectionTypeLabels,
  getPostalAddress,
  formatDate,
  getLifeSupportServices,
  getHasElectricity,
  getIsGasLineOnly,
} from "./helper";
import EditButton from "./EditButton";
import ReadOnlySecondaryAccountHolder from "./ReadOnlySecondaryAccountHolder";
import ReadOnlyBusinessPrimaryContact from "./ReadOnlyBusinessPrimaryContact";
import ImportantThingsToKnow from "./ImportantThingsToKnow";
import { getConcessionCardLabel } from "./concessionCardTypes";
import { AsyncButton } from "src/components/AsyncButton";

interface EICFormPageFiveProps {
  onBack: () => void;
  onSubmit: (values: EICFormData) => Promise<void>;
  onEdit: (page: number) => void;
  isReadonlyFlow: boolean;
}

const EICFormPageFive: React.FC<EICFormPageFiveProps> = ({
  onBack,
  onSubmit,
  onEdit,
  isReadonlyFlow,
}) => {
  const { values } = useFormikContext<EICFormData>();

  const hasElectricityConnection = getHasElectricity(values.connectionTypes);
  const hasGasConnection = values.connectionTypes.some((type) =>
    [
      ConnectionTypeEnum.METER_FIX,
      ConnectionTypeEnum.SERVICE_CONNECTION_AND_METER_FIX,
      ConnectionTypeEnum.SERVICE_CONNECTION,
    ].includes(type)
  );

  const hasOnlyGasLine = useMemo(
    () => getIsGasLineOnly(values.connectionTypes),
    [values.connectionTypes]
  );

  return (
    <Stack
      space={["xlarge", "xxlarge"]}
      data-role="section"
      id="eic-form-page-five"
    >
      {/* Content */}
      <Stack>
        <Heading variant="h3">Review and confirm</Heading>
        <Text>
          Almost there! Review your details about your new account then submit.
        </Text>
      </Stack>
      {/* Content */}

      {/* Request details */}
      <Stack space="large">
        <Heading variant="h4">Premise details</Heading>
        {/* Fields */}
        {/* Premise address */}
        <ReadOnlyTextField label="Premise address" value={values.address} />
        {/* End Premise address */}

        {/* What's being connected */}
        <ReadOnlyTextField
          label="What's being connected?"
          value={getConnectionTypeLabels(values.connectionTypes, true)}
        />

        {/* End What's being connected */}
        {/* End Fields */}
      </Stack>
      {/* End Request details */}

      {/* What you told us */}
      <Stack space={["large", "xlarge"]}>
        <Heading variant="h4">What you told us</Heading>

        {/* Account holder */}
        <Stack>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Text weight="medium" tone="neutralDark">
              Account holder
            </Text>
            {isReadonlyFlow ? null : <EditButton onClick={() => onEdit(2)} />}
          </Box>

          <Stack>
            {/* Account type */}
            <ReadOnlyTextField
              label="Account holder type"
              value={
                values.accountType === "personal" ? "Personal" : "Business"
              }
            />
            {/* End Account type */}

            {/* Name/Company name */}
            <ReadOnlyTextField
              label={
                values.accountType === "personal" ? "Name" : "Company name"
              }
              value={
                values.accountType === "personal"
                  ? `${values.firstName} ${values.lastName}`
                  : values.companyName
              }
            />
            {/* End Name/Company name */}

            {values.accountType === "personal" && (
              <Box
                display="flex"
                flexDirection={{
                  xs: "column",
                  sm: "row",
                }}
              >
                <Columns>
                  <Column>
                    {/* DOB + Phone */}
                    {/* Date of birth */}
                    <ReadOnlyTextField
                      label="Date of birth"
                      value={formatDate(values.dateOfBirth)}
                    />
                  </Column>
                  <Column>
                    {/* End Date of birth */}
                    {/* Phone */}
                    <ReadOnlyTextField label="Phone" value={values.phone} />
                    {/* End Phone */}
                    {/* End DOB + Phone */}
                  </Column>
                </Columns>
              </Box>
            )}

            {values.accountType === "business" && (
              <ReadOnlyTextField label="ABN" value={values.abn} />
            )}

            {/* Email */}
            {values.accountType === "personal" && (
              <ReadOnlyTextField label="Email" value={values.email} />
            )}
            {/* End Email */}
          </Stack>
        </Stack>
        {/* End Account holder */}

        {/* Secondary Account holder */}
        {values.addSecondaryAccountHolder && (
          <ReadOnlySecondaryAccountHolder
            secondaryFirstName={values.secondaryFirstName}
            secondaryLastName={values.secondaryLastName}
            secondaryDateOfBirth={values.secondaryDateOfBirth}
            secondaryPhone={values.secondaryPhone}
            secondaryEmail={values.secondaryEmail}
            formatDate={formatDate}
            onEdit={isReadonlyFlow ? undefined : () => onEdit(2)}
          />
        )}
        {/* End Secondary Account holder */}

        {values.accountType === "business" && (
          <ReadOnlyBusinessPrimaryContact
            firstName={values.firstName}
            lastName={values.lastName}
            dateOfBirth={values.dateOfBirth}
            phone={values.phone}
            email={values.email}
            formatDate={formatDate}
            onEdit={isReadonlyFlow ? undefined : () => onEdit(2)}
          />
        )}
        {/* End Primary contact */}

        {/* Bills & Letters */}
        <Stack>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Text weight="medium" tone="neutralDark">
              Your bills and letters
            </Text>
            {isReadonlyFlow ? null : <EditButton onClick={() => onEdit(3)} />}
          </Box>

          <Stack>
            {/* Comms preference */}
            <ReadOnlyTextField
              label="Send communications including bills by"
              value={values.billDeliveryMethod === "post" ? "Post" : "Email"}
            />
            {/* End Comms preference */}
            {/* Postal address */}
            <ReadOnlyTextField
              label="Postal address"
              value={getPostalAddress(values)}
            />
            {/* End Postal address */}
          </Stack>
        </Stack>
        {/* End Bills & Letters */}

        {/* Concession  */}
        <Stack>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Text weight="medium" tone="neutralDark">
              Concession
            </Text>
            {isReadonlyFlow ? null : <EditButton onClick={() => onEdit(3)} />}
          </Box>
          <Stack>
            <ReadOnlyTextField
              label="Concession eligible"
              value={values.concessionStatus === true ? "Yes" : "No"}
            />
            {values.concessionStatus === true && values.concessionCardType && (
              <ReadOnlyTextField
                label="Concession card type"
                value={getConcessionCardLabel(values.concessionCardType)}
              />
            )}
            {values.concessionStatus === true && (
              <ReadOnlyTextField
                label="Concession number"
                value={values.concessionNumber}
              />
            )}
          </Stack>
        </Stack>
        {/* End of Concession */}

        {/* Life support */}
        {hasOnlyGasLine || values.state !== State.VIC ? null : (
          <Stack>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Text weight="medium" tone="neutralDark">
                Life support
              </Text>
              <EditButton onClick={() => onEdit(3)} />
            </Box>
            <Stack>
              {/* Life support required */}
              <ReadOnlyTextField
                label="Life support required"
                value={values.lifeSupport === "yes" ? "Yes" : "No"}
              />
              {/* End Life support required */}
              {values.whichEnergy && (
                <ReadOnlyTextField
                  label="Service(s) that run life support equipment"
                  value={getLifeSupportServices(values)}
                />
              )}
              {/* End Services */}
            </Stack>
          </Stack>
        )}
        {/* End Life support */}
      </Stack>
      {/* End What you told us */}

      {/* Important things to know */}
      <ImportantThingsToKnow
        hasElectricityConnection={hasElectricityConnection}
        hasGasConnection={hasGasConnection}
        state={values.state}
        connectionTypes={values.connectionTypes}
        postcode={values.postcode}
        requestId={values.referenceNumber}
      />
      {/* End Important things to know */}

      {/* Actions  */}
      <Box
        display="flex"
        justifyContent="space-between"
        flexDirection={{ xs: "column", md: "row-reverse" }}
      >
        <AsyncButton onHandleClick={async () => await onSubmit(values)}>
          Accept and submit
        </AsyncButton>
        <Button variant="outlined" onClick={onBack}>
          Back
        </Button>
      </Box>
      {/* End Actions */}
    </Stack>
  );
};

export default EICFormPageFive;
